import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, ChevronLeft, ChevronRight, Plus, Target, TrendingUp, Compass, Clock,
  AlertTriangle, Hourglass, X, Loader2, Lightbulb, Brain, Trash2, Check, Calendar, Mic, Send, Volume2,
  Phone, PhoneOff, CalendarClock, ListChecks, Trophy, Flame, ArrowRight,
} from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLES = {
  today: 'text-indigo-600 font-bold', done: 'text-emerald-600 font-semibold',
  planned: 'text-slate-400 font-medium',
};
const TIMELINE_KIND = {
  plan: 'text-indigo-600', task: 'text-slate-700', break: 'text-emerald-600',
  follow: 'text-amber-600', wrap: 'text-slate-500',
};
const PATTERN_ICON = {
  win: <Check className="w-3.5 h-3.5 text-emerald-500" />,
  slip: <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />,
  wait: <Hourglass className="w-3.5 h-3.5 text-slate-400" />,
  snooze: <Clock className="w-3.5 h-3.5 text-amber-500" />,
  grow: <TrendingUp className="w-3.5 h-3.5 text-indigo-500" />,
};

export default function MyWeek() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const nav = useNavigate();
  const [beta, setBeta] = useState(null);
  const [data, setData] = useState(null);
  const [pros, setPros] = useState(null);
  const [ptargets, setPtargets] = useState([]);
  const [targetsModal, setTargetsModal] = useState(false);
  const [logModal, setLogModal] = useState(false);
  const [offset, setOffset] = useState(0);
  const [ask, setAsk] = useState(null);
  const [showGoal, setShowGoal] = useState(false);
  const [showMemory, setShowMemory] = useState(false);
  const [workingEdit, setWorkingEdit] = useState(null);
  const [whyOpen, setWhyOpen] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [plan, setPlan] = useState(null); // {intro, blocks}
  const [review, setReview] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [andiInput, setAndiInput] = useState('');
  const [voiceModal, setVoiceModal] = useState(null); // { voices, selected, saving }

  const openVoicePicker = async () => {
    setVoiceModal({ voices: [], selected: null, saving: false, loading: true });
    try {
      const [{ data: v }, { data: p }] = await Promise.all([
        axios.get(`${API}/api/voice/voices`, { headers }),
        axios.get(`${API}/api/voice/preference`, { headers }),
      ]);
      setVoiceModal({ voices: v.voices || [], selected: p.voice || v.default, saving: false });
    } catch { toast.error('Could not load voices'); setVoiceModal(null); }
  };
  const saveVoice = async (voiceId) => {
    setVoiceModal((m) => ({ ...m, selected: voiceId, saving: true }));
    try { await axios.post(`${API}/api/voice/preference`, { voice: voiceId }, { headers }); toast.success('Hey Andi voice updated'); setVoiceModal(null); }
    catch { toast.error('Could not save'); setVoiceModal((m) => ({ ...m, saving: false })); }
  };

  const openAndi = (message) => window.dispatchEvent(new CustomEvent('hey-andi:open', message ? { detail: { message } } : undefined));
  const submitAndi = (e) => { e?.preventDefault(); const v = andiInput.trim(); openAndi(v || undefined); setAndiInput(''); };

  const load = useCallback(async () => {
    try {
      const [{ data: d }, pr, tg] = await Promise.all([
        axios.get(`${API}/api/myweek/overview?week_offset=${offset}`, { headers }),
        axios.get(`${API}/api/myweek/prospecting`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/api/myweek/prospecting/targets`, { headers }).catch(() => ({ data: { targets: [] } })),
      ]);
      setData(d); setPros(pr.data); setPtargets(tg.data?.targets || []); setBeta(true);
    } catch (e) {
      if (e?.response?.status === 403) setBeta(false);
      else toast.error('Could not load MyWeek');
    }
  }, [headers, offset]);
  useEffect(() => { load(); }, [load]);

  const addTarget = async (label, target) => {
    try { const { data } = await axios.post(`${API}/api/myweek/prospecting/targets`, { label, target }, { headers }); setPtargets(data.targets || []); }
    catch { toast.error('Could not add target'); }
  };
  const updateTarget = async (id, label, target) => {
    try { const { data } = await axios.put(`${API}/api/myweek/prospecting/targets/${id}`, { label, target }, { headers }); setPtargets(data.targets || []); }
    catch { toast.error('Could not update target'); }
  };
  const deleteTarget = async (id) => {
    try { const { data } = await axios.delete(`${API}/api/myweek/prospecting/targets/${id}`, { headers }); setPtargets(data.targets || []); }
    catch { toast.error('Could not remove target'); }
  };
  const logActivity = async (entries) => {
    try { const { data } = await axios.post(`${API}/api/myweek/prospecting/log`, { entries }, { headers }); setPtargets(data.targets || []); toast.success('Activity logged'); setLogModal(false); }
    catch { toast.error('Could not log activity'); }
  };

  const planMyDay = async () => {
    setPlanning(true);
    try {
      const { data: r } = await axios.post(`${API}/api/myweek/plan-day`, {}, { headers });
      setPlan(r);
      toast.success('Your day is planned');
    } catch { toast.error('Could not plan your day'); }
    finally { setPlanning(false); }
  };

  const act = async (item, kind, status) => {
    if (!item?.key) return;
    try {
      await axios.post(`${API}/api/myweek/action`, { item_key: item.key, kind, status, label: item.title, deal_id: item.deal_id || null }, { headers });
      toast.success(status === 'done' ? 'Nice — cleared' : 'Snoozed till tomorrow');
      load();
    } catch { toast.error('Could not update'); }
  };
  const undoAct = async (key) => {
    try { await axios.delete(`${API}/api/myweek/action/${encodeURIComponent(key)}`, { headers }); load(); }
    catch { toast.error('Could not undo'); }
  };

  const runReview = async () => {
    setReviewing(true); setReview({ loading: true });
    try { const { data: r } = await axios.post(`${API}/api/myweek/weekly-review`, {}, { headers }); setReview(r); }
    catch { toast.error('Weekly review failed'); setReview(null); }
    finally { setReviewing(false); }
  };

  const runBriefing = async () => {
    setBriefing({ loading: true });
    try { const { data: b } = await axios.post(`${API}/api/myweek/daily-briefing`, {}, { headers }); setBriefing(b); }
    catch { toast.error('Could not build your briefing'); setBriefing(null); }
  };

  const setFocus = async (focus) => { await axios.post(`${API}/api/myweek/profile`, { focus }, { headers }); load(); };
  const saveWorking = async () => { await axios.post(`${API}/api/myweek/profile`, { working_on: workingEdit }, { headers }); setWorkingEdit(null); load(); };
  const delGoal = async (id) => { await axios.delete(`${API}/api/myweek/goals/${id}`, { headers }); load(); };
  const delMemory = async (id) => { await axios.delete(`${API}/api/myweek/memory/${id}`, { headers }); load(); };

  if (beta === false) return (
    <div className="min-h-screen bg-slate-50"><Header />
      <div className="max-w-md mx-auto mt-24 text-center px-6" data-testid="myweek-beta-off">
        <Compass className="w-12 h-12 mx-auto text-indigo-400" />
        <h1 className="text-2xl font-black text-slate-900 mt-4">MyWeek is in Beta</h1>
        <p className="text-slate-500 mt-2">Ask your administrator to switch it on for your account.</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-slate-50"><Header /><div className="flex justify-center mt-32"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></div>;

  const w = data.week;
  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="myweek-page"><Header />
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-5">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">MyWeek</h1>
            <p className="text-slate-500 text-sm">Your personal AI operating system</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-right">
              <div className="flex items-center gap-2">
                <button onClick={() => { setPlan(null); setOffset(offset - 1); }} data-testid="mw-prev-week" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-sm font-bold text-slate-800">{w.start} – {w.end}</span>
                <button onClick={() => { setPlan(null); setOffset(offset + 1); }} data-testid="mw-next-week" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><ChevronRight className="w-4 h-4" /></button>
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">{w.day.split(' ').slice(0, 1)} · {w.pct}% through</p>
              <div className="w-40 h-1.5 bg-slate-200 rounded-full mt-1 ml-auto overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${w.pct}%` }} /></div>
            </div>
            {data.wins_today > 0 && <span data-testid="mw-wins-today" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-sm font-bold text-emerald-700"><Check className="w-4 h-4" /> {data.wins_today} cleared today</span>}
            <button onClick={runBriefing} data-testid="mw-daily-briefing" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"><Sparkles className="w-4 h-4" /> Daily Briefing</button>
            <button onClick={runReview} data-testid="mw-weekly-review" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50"><TrendingUp className="w-4 h-4" /> Weekly Review</button>
            <button onClick={() => setShowGoal(true)} data-testid="mw-new" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50"><Plus className="w-4 h-4" /> New</button>
            <button onClick={openVoicePicker} data-testid="mw-voice-picker" title="Hey Andi voice" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50"><Volume2 className="w-4 h-4" /> <span className="hidden sm:inline">Voice</span></button>
            <button onClick={() => openAndi()} data-testid="mw-hey-andi" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"><Sparkles className="w-4 h-4" /> Hey Andi</button>
          </div>
        </div>

        {/* Hero */}
        <div className="cp-dark mt-5 rounded-2xl text-white p-6 sm:p-8" style={{ background: 'linear-gradient(to bottom right, #0f172a, #1e293b)' }} data-testid="myweek-hero">
          <h2 className="text-2xl sm:text-3xl font-black">Good day, {data.user.first_name}.</h2>
          <p className="text-slate-300 text-sm mt-0.5">Here's where your week stands.</p>
          <div className="mt-4 bg-white/10 backdrop-blur rounded-xl p-4 max-w-3xl">
            <p className="text-sm leading-relaxed text-white/90" data-testid="mw-hero-summary">{(plan?.intro && plan.intro.trim()) ? plan.intro : data.myweek_says}</p>
            <div className="flex gap-2 mt-3 flex-wrap">
              <button onClick={planMyDay} disabled={planning} data-testid="mw-plan-day" className="px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-xs font-bold inline-flex items-center gap-1.5">{planning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />} Plan My Day</button>
              <button onClick={() => openAndi('Why am I tracking the way I am this week?')} className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-xs font-bold">Why?</button>
              <button onClick={() => setWorkingEdit(data.working_on)} className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-xs font-bold">Change My Focus</button>
            </div>
          </div>
          {data.next_best_action && (
            <div className="mt-4 bg-indigo-500/20 border border-indigo-400/30 rounded-xl p-4 max-w-3xl" data-testid="mw-next-best-action">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-indigo-200">Next Best Action</p>
                  <p className="text-base font-bold text-white mt-0.5">{data.next_best_action.title}</p>
                  <p className="text-xs text-white/70 mt-0.5">{data.next_best_action.client} · {data.next_best_action.priority}</p>
                  <p className="text-[11px] text-white/60 mt-1 leading-relaxed">{data.next_best_action.why}</p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button onClick={() => nav(data.next_best_action.path)} data-testid="mw-nba-action" className="px-3 py-1.5 rounded-lg bg-white text-slate-900 text-xs font-bold hover:bg-slate-100">{data.next_best_action.action}</button>
                  <div className="flex gap-1.5">
                    <button onClick={() => act(data.next_best_action, 'task', 'done')} data-testid="mw-nba-done" className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-[11px] font-bold"><Check className="w-3 h-3" /> Done</button>
                    <button onClick={() => act(data.next_best_action, 'task', 'snoozed')} data-testid="mw-nba-snooze" className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-lg bg-white/15 hover:bg-white/25 text-white text-[11px] font-bold"><Clock className="w-3 h-3" /> Snooze</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* PROSPECTING TODAY — live Lead Nurture sales accountability */}
        {pros && <ProspectingToday pros={pros} targets={ptargets} onSetTargets={() => setTargetsModal(true)} onLog={() => setLogModal(true)} onAskAndi={openAndi} nav={nav} />}

        {/* 3 columns */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* LEFT */}
          <div className="space-y-5">
            <Card title="This Week's Goals" action={<button onClick={() => setShowGoal(true)} className="text-xs font-bold text-indigo-600" data-testid="mw-add-goal">+ Add</button>} testId="myweek-goals">
              {data.goals.length === 0 && <Empty>Set your first goal — a lodgement target, a conversion rate, or anything you're aiming for.</Empty>}
              <div className="space-y-3">
                {data.goals.map((g) => {
                  const pct = g.target ? Math.min(100, Math.round(((g.current || 0) / g.target) * 100)) : null;
                  const fmt = (v) => g.unit === 'money' ? money(v) : g.unit === 'percent' ? `${v}%` : v;
                  return (
                    <div key={g.id} className="group" data-testid={`mw-goal-${g.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold text-slate-800">{g.title}</p>
                        <button onClick={() => delGoal(g.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      {g.target != null && <p className="text-xs text-slate-500">{fmt(g.current || 0)} / {fmt(g.target)}</p>}
                      {pct != null && <div className="w-full h-2 bg-slate-100 rounded-full mt-1 overflow-hidden"><div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} /></div>}
                      {g.note && <p className="text-[11px] text-slate-400 mt-0.5">{g.note}</p>}
                    </div>
                  );
                })}
              </div>
            </Card>
            <Card title="What I'm Working On" action={<button onClick={() => setWorkingEdit(data.working_on)} className="text-xs font-bold text-indigo-600" data-testid="mw-manage-working">Manage</button>}>
              {data.working_on.length === 0 ? <Empty>Improvement areas you're focused on — e.g. "Delegate more credit work".</Empty> :
                <ul className="space-y-2">{data.working_on.map((x, i) => <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />{x}</li>)}</ul>}
            </Card>
            <Card title="My Focus">
              <select value={data.focus} onChange={(e) => setFocus(e.target.value)} data-testid="mw-focus" className="w-full text-sm px-3 py-2 rounded-xl border border-slate-200 bg-white font-semibold text-slate-800">
                {data.focus_options.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Card>
          </div>

          {/* CENTRE */}
          <div className="space-y-5">
            <Card title="Today" subtitle={data.week.day} testId="myweek-today">
              <div className="flex items-center gap-2 mb-2"><span className="text-amber-500">★</span><p className="text-sm font-black text-slate-800">Your Top 3</p></div>
              {data.top3.length === 0 && <Empty>As your applications and portals progress, your three highest-impact actions appear here.</Empty>}
              <div className="space-y-2">
                {data.top3.map((t, i) => (
                  <div key={t.id} className="rounded-xl border border-slate-200 p-3" data-testid={`mw-top3-${i}`}>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-slate-800">{t.title}</p>
                        <p className="text-[11px] text-slate-500">{t.subtitle}</p>
                        {whyOpen === t.id && <p className="text-[11px] text-indigo-700 bg-indigo-50 rounded-lg p-2 mt-1.5">{t.why}</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs font-bold text-slate-700">{t.impact}</p>
                        <button onClick={() => nav(t.path)} data-testid={`mw-top3-action-${i}`} className="mt-1 px-3 py-1 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700">{t.action}</button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <button onClick={() => setWhyOpen(whyOpen === t.id ? null : t.id)} className="text-[11px] text-slate-400 hover:text-indigo-600">Why this?</button>
                      <span className="text-slate-200">·</span>
                      <button onClick={() => act(t, 'top3', 'done')} data-testid={`mw-top3-done-${i}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 hover:text-emerald-700"><Check className="w-3 h-3" /> Mark done</button>
                      <button onClick={() => act(t, 'top3', 'snoozed')} data-testid={`mw-top3-snooze-${i}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-slate-600"><Clock className="w-3 h-3" /> Snooze</button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card title="Your Day" subtitle={plan ? 'Planned' : undefined} action={(plan || (data.timeline && data.timeline.length > 0)) ? <button onClick={planMyDay} disabled={planning} className="text-xs font-bold text-indigo-600" data-testid="mw-replan">{planning ? '…' : 'Re-plan'}</button> : undefined} testId="myweek-yourday">
              {(() => {
                const blocks = plan?.blocks || data.timeline || [];
                if (blocks.length === 0) return <Empty>Tap “Plan My Day” and MyWeek will build a focused, time-blocked schedule from your live pipeline and goals.</Empty>;
                return (
                  <ul className="space-y-2" data-testid="mw-timeline">
                    {blocks.map((t, i) => (
                      <li key={i} className="flex items-start gap-3" data-testid={`mw-timeline-${i}`}>
                        <span className={`w-14 shrink-0 text-xs font-bold ${TIMELINE_KIND[t.kind] || 'text-slate-500'}`}>{t.time}</span>
                        <div className="min-w-0 flex-1 pb-1 border-b border-slate-100">
                          <p className="text-sm font-semibold text-slate-800">{t.title}</p>
                          {t.detail && <p className="text-[11px] text-slate-400">{t.detail}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </Card>
          </div>

          {/* RIGHT */}
          <div className="space-y-5">
            <Card title="MyWeek Says" icon={<Lightbulb className="w-4 h-4 text-amber-500" />} testId="myweek-says">
              <p className="text-sm text-slate-700 leading-relaxed">{data.myweek_says}</p>
            </Card>
            {data.patterns && data.patterns.length > 0 && (
              <Card title="Patterns MyWeek Is Noticing" icon={<Brain className="w-4 h-4 text-indigo-500" />} testId="myweek-patterns">
                <ul className="space-y-2.5">
                  {data.patterns.map((p, i) => (
                    <li key={i} className="flex items-start gap-2" data-testid={`mw-pattern-${i}`}>
                      <span className="mt-1 shrink-0">{PATTERN_ICON[p.icon] || <Lightbulb className="w-3.5 h-3.5 text-slate-400" />}</span>
                      <p className="text-[13px] text-slate-600 leading-relaxed">{p.text}</p>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
            <Card title="Things You're Avoiding" icon={<AlertTriangle className="w-4 h-4 text-rose-400" />} testId="myweek-avoiding">
              {data.avoiding.length === 0 ? <Empty>Tasks that go overdue or slip past SLA surface here — neutrally, to help you clear them.</Empty> :
                <ul className="space-y-1">{data.avoiding.map((a, i) => (
                  <li key={i} data-testid={`mw-avoiding-${i}`} className={`group flex items-start gap-2 p-2 -mx-2 rounded-lg hover:bg-rose-50 ${a.snoozed ? 'opacity-50' : ''}`}>
                    <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${a.health === 'OVERDUE' ? 'bg-rose-500' : 'bg-amber-500'}`} />
                    <div onClick={() => nav(a.path)} className="min-w-0 flex-1 cursor-pointer">
                      <p className="text-sm font-semibold text-slate-800 truncate">{a.title}{a.snoozed && <span className="ml-1 text-[10px] text-slate-400">· snoozed</span>}</p>
                      <p className="text-[11px] text-slate-400 truncate">{a.subtitle}</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); act(a, 'avoiding', 'done'); }} data-testid={`mw-avoiding-done-${i}`} title="Mark done" className="shrink-0 opacity-0 group-hover:opacity-100 p-1 rounded-md text-emerald-500 hover:bg-emerald-100"><Check className="w-3.5 h-3.5" /></button>
                  </li>
                ))}</ul>}
            </Card>
            <Card title="Waiting On" icon={<Hourglass className="w-4 h-4 text-slate-400" />} testId="myweek-waiting">
              {data.waiting_on.length === 0 ? <Empty>Clients, lenders or staff you're waiting on — so MyWeek won't nag you to chase what's legitimately pending.</Empty> :
                <ul className="space-y-2">{data.waiting_on.map((x, i) => (
                  <li key={i} onClick={() => nav(x.path)} data-testid={`mw-waiting-${i}`} className="group flex items-start gap-2 p-2 -mx-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800 truncate">{x.name}</p>
                      <p className="text-[11px] text-slate-400 truncate">{x.what}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">{x.party}</span>
                      <p className="text-[10px] text-slate-400 mt-0.5">{x.days}d</p>
                    </div>
                  </li>
                ))}</ul>}
            </Card>
            <button onClick={() => setShowMemory(true)} data-testid="mw-memory-btn" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50"><Brain className="w-4 h-4 text-indigo-500" /> What MyWeek Knows About Me</button>
          </div>
        </div>

        {/* This Week strip */}
        <Card title="This Week" className="mt-5" testId="myweek-week-strip">
          <div className="grid grid-cols-7 gap-2 text-center">
            {data.week_days.map((d, i) => (
              <div key={i} className={`rounded-xl p-2 ${d.status === 'today' ? 'bg-indigo-50 ring-1 ring-indigo-200' : 'bg-slate-50'}`}>
                <p className="text-[11px] text-slate-400">{d.label}</p>
                <p className="text-sm font-bold text-slate-700">{d.date.split(' ')[0]}</p>
                <p className={`text-[10px] mt-0.5 ${STATUS_STYLES[d.status]}`}>{d.status === 'today' ? 'Today' : d.status === 'done' ? '—' : 'Planned'}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Outcomes + trajectory */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <Card title="Weekly Outcomes" testId="myweek-outcomes">
              {data.outcomes.length === 0 ? <Empty>Once you have lodgements, proposals and completed portals this week, your numbers appear here.</Empty> :
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {data.outcomes.map((o, i) => (
                    <div key={i} className="rounded-xl bg-slate-50 p-3"><p className="text-[11px] text-slate-400">{o.label}</p><p className="text-lg font-black text-slate-800">{o.value}</p></div>
                  ))}
                </div>}
            </Card>
          </div>
          <Card title="Where You're Heading" icon={<TrendingUp className="w-4 h-4 text-emerald-500" />}>
            {!data.trajectory ? <Empty>Add a target goal (e.g. lodgements) and MyWeek will project where you'll land and how to close the gap.</Empty> :
              <div>
                <p className="text-sm font-bold text-slate-800">{data.trajectory.title}</p>
                <div className="flex gap-4 mt-2 text-sm"><span className="text-slate-500">Now <b className="text-slate-800">{data.trajectory.current}</b></span><span className="text-slate-500">Likely <b className="text-emerald-600">{data.trajectory.likely}</b></span><span className="text-slate-500">Gap <b className="text-rose-600">{data.trajectory.gap}</b></span></div>
                <p className="text-[11px] text-slate-500 mt-2">{data.trajectory.note}</p>
              </div>}
          </Card>
        </div>
      </div>

      {/* Ask MyWeek */}
      {review && <Modal onClose={() => setReview(null)} title="Your Weekly Review" subtitle={review.week ? `${review.week.start} – ${review.week.end}` : 'This week'} testId="myweek-review">
        {review.loading || reviewing ? <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Reflecting on your week…</div> :
          <div>
            <div className="flex gap-3 mb-3">
              <div className="flex-1 rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-center"><p className="text-2xl font-black text-emerald-600" data-testid="mw-review-wins">{review.wins_count}</p><p className="text-[11px] text-emerald-700 font-semibold">Cleared</p></div>
              <div className="flex-1 rounded-xl bg-rose-50 border border-rose-100 p-3 text-center"><p className="text-2xl font-black text-rose-600">{review.overdue}</p><p className="text-[11px] text-rose-700 font-semibold">Still past SLA</p></div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="mw-review-text"><p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{review.review}</p></div>
            {review.wins && review.wins.length > 0 && <div className="mt-3"><p className="text-[11px] font-bold text-slate-400 mb-1.5">What you cleared</p><ul className="space-y-1">{review.wins.map((w, i) => <li key={i} className="flex items-center gap-2 text-[13px] text-slate-600"><Check className="w-3 h-3 text-emerald-500 shrink-0" />{w}</li>)}</ul></div>}
          </div>}
      </Modal>}
      {targetsModal && <SetTargetsModal targets={ptargets} onAdd={addTarget} onUpdate={updateTarget} onDelete={deleteTarget} onClose={() => setTargetsModal(false)} />}
      {logModal && <LogActivityModal targets={ptargets} onLog={logActivity} onClose={() => setLogModal(false)} />}
      {briefing && <Modal onClose={() => setBriefing(null)} title="Daily Business Briefing" subtitle={briefing.day || 'Today'} testId="myweek-briefing">
        {briefing.loading ? <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Pulling together your day…</div> :
          <div className="space-y-4">
            {briefing.credit_paused && <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] font-semibold text-amber-700 flex items-center gap-1.5" data-testid="mw-briefing-credit-paused"><AlertTriangle className="w-3.5 h-3.5" /> Credit queue is paused — credit-team items are intentionally hidden.</div>}
            <div className="rounded-xl p-4" style={{ backgroundColor: '#0f172a', color: '#ffffff' }} data-testid="mw-briefing-narrative"><p className="text-sm leading-relaxed whitespace-pre-wrap">{briefing.briefing}</p></div>
            {briefing.numbers?.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="mw-briefing-numbers">
                {briefing.numbers.map((n, i) => (
                  <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 leading-tight">{n.label}</p>
                    <p className="text-sm font-black text-slate-900 mt-0.5">{n.value}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <BriefCol title="What happened" tone="text-emerald-600" testId="mw-briefing-happened">
                {briefing.what_happened?.length ? briefing.what_happened.map((h, i) => <li key={i} className="text-[13px] text-slate-700 leading-snug flex gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />{h}</li>) : <Empty>A quiet start so far.</Empty>}
              </BriefCol>
              <BriefCol title="Needs attention" tone="text-rose-600" testId="mw-briefing-attention">
                {briefing.needs_attention?.length ? briefing.needs_attention.map((a, i) => <li key={i} onClick={() => a.path && nav(a.path)} className="text-[13px] text-slate-700 leading-snug flex gap-1.5 cursor-pointer hover:text-indigo-600"><AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" /><span><b>{a.title}</b>{a.sub ? <span className="text-slate-400"> · {a.sub}</span> : ''}</span></li>) : <Empty>Nothing urgent right now.</Empty>}
              </BriefCol>
              <BriefCol title="Do next" tone="text-indigo-600" testId="mw-briefing-next">
                {briefing.do_next?.length ? briefing.do_next.map((d, i) => <li key={i} onClick={() => d.path && nav(d.path)} className="text-[13px] text-slate-700 leading-snug flex gap-1.5 cursor-pointer hover:text-indigo-600"><ArrowRight className="w-3.5 h-3.5 text-indigo-500 mt-0.5 shrink-0" /><span><b>{d.title}</b>{d.sub ? <span className="text-slate-400"> · {d.sub}</span> : ''}</span></li>) : <Empty>Queue is clear.</Empty>}
              </BriefCol>
            </div>
            <button onClick={() => { setBriefing(null); openAndi('Give me a verbal briefing on my day'); }} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700" data-testid="mw-briefing-ask-andi"><Sparkles className="w-4 h-4" /> Ask Hey Andi to go deeper</button>
          </div>}
      </Modal>}
      {ask && <Modal onClose={() => setAsk(null)} title="Hey Andi" subtitle="Answers from your own goals, focus and activity" testId="myweek-ask">
        <div className="text-center py-6">
          <Sparkles className="w-8 h-8 mx-auto text-indigo-400" />
          <p className="text-sm text-slate-600 mt-3">Hey Andi now lives in a conversation panel — chat or talk to it without leaving MyWeek.</p>
          <button onClick={() => { setAsk(null); openAndi(); }} className="mt-4 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold">Open Hey Andi</button>
        </div>
      </Modal>}

      {/* Goal modal */}
      {showGoal && <GoalModal headers={headers} onClose={() => setShowGoal(false)} onSaved={() => { setShowGoal(false); load(); }} />}

      {/* Working-on editor */}
      {workingEdit !== null && <Modal onClose={() => setWorkingEdit(null)} title="What I'm Working On" testId="myweek-working-edit">
        <p className="text-xs text-slate-500 mb-2">One improvement area per line. MyWeek uses these to shape its recommendations.</p>
        <textarea rows={5} value={workingEdit.join('\n')} onChange={(e) => setWorkingEdit(e.target.value.split('\n'))} data-testid="mw-working-input" className="w-full text-sm px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-indigo-400" placeholder={'Delegate more credit work\nProtect morning sales time'} />
        <button onClick={saveWorking} data-testid="mw-working-save" className="mt-3 w-full px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold">Save</button>
      </Modal>}

      {/* Memory modal */}
      {showMemory && <Modal onClose={() => setShowMemory(false)} title="What MyWeek Knows About Me" subtitle="Edit or remove anything — nothing is hidden" testId="myweek-memory">
        <MemoryEditor data={data.memory} headers={headers} onChange={load} onDelete={delMemory} />
      </Modal>}

      {/* Hey Andi voice picker */}
      {voiceModal && <Modal onClose={() => setVoiceModal(null)} title="Hey Andi · Voice" subtitle="Choose how Andi sounds when you talk" testId="myweek-voice-modal">
        {voiceModal.loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
          <div className="grid grid-cols-2 gap-2 max-h-[52vh] overflow-y-auto pr-1">
            {voiceModal.voices.map((v) => {
              const id = v.voice_id || v.id || v.name;
              const sel = voiceModal.selected === id;
              return (
                <button key={id} onClick={() => saveVoice(id)} disabled={voiceModal.saving} data-testid={`mw-voice-${id}`}
                  className={`flex items-center gap-2 p-3 rounded-xl border text-left transition ${sel ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500/30' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <span className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${sel ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`}><Volume2 className="w-4 h-4" /></span>
                  <div className="min-w-0"><p className="text-sm font-semibold text-slate-800 truncate capitalize">{(v.name || id)}</p>{v.language && <p className="text-[11px] text-slate-400">{v.language}</p>}</div>
                  {sel && <Check className="w-4 h-4 text-indigo-600 ml-auto shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </Modal>}

      {/* Persistent Hey Andi input */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-gradient-to-t from-slate-50 via-slate-50/95 to-transparent pt-6 pb-[max(14px,env(safe-area-inset-bottom))] px-4 pointer-events-none" data-testid="myweek-andi-bar">
        <form onSubmit={submitAndi} className="max-w-2xl mx-auto pointer-events-auto">
          <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.35)] px-2 py-1.5">
            <span className="cp-dark w-8 h-8 rounded-xl bg-[#0B1220] text-white grid place-items-center text-[12px] font-black shrink-0">A</span>
            <input value={andiInput} onChange={(e) => setAndiInput(e.target.value)} data-testid="mw-andi-input" placeholder="Ask Andi anything…"
              className="flex-1 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400 px-1" />
            <button type="button" onClick={() => openAndi()} data-testid="mw-andi-mic" aria-label="Talk to Andi" className="w-9 h-9 rounded-xl grid place-items-center text-slate-500 hover:bg-slate-100"><Mic className="w-4 h-4" /></button>
            <button type="submit" data-testid="mw-andi-send" aria-label="Send to Andi" className="w-9 h-9 rounded-xl grid place-items-center bg-indigo-600 text-white hover:bg-indigo-700"><Send className="w-4 h-4" /></button>
          </div>
        </form>
      </div>
    </div>
  );
}

const money = (n) => !n ? '$0' : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}m` : n >= 1e3 ? `$${Math.round(n / 1e3)}k` : `$${n}`;

const Card = ({ title, subtitle, action, icon, children, className = '', testId }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm p-5 ${className}`} data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-1.5">{icon}<h3 className="text-sm font-black text-slate-800">{title}</h3>{subtitle && <span className="text-xs text-slate-400 ml-1">{subtitle}</span>}</div>
      {action}
    </div>
    {children}
  </div>
);
const Empty = ({ children }) => <p className="text-xs text-slate-400 leading-relaxed py-1">{children}</p>;

const BriefCol = ({ title, tone, children, testId }) => (
  <div className="rounded-xl border border-slate-200 p-3" data-testid={testId}>
    <p className={`text-[11px] font-black uppercase tracking-wide mb-2 ${tone}`}>{title}</p>
    <ul className="space-y-1.5">{children}</ul>
  </div>
);

const PROS_TILES = [
  { k: 'contacts', label: 'Contacts Made', icon: Phone, tone: 'text-indigo-600' },
  { k: 'connects', label: 'Connects', icon: Phone, tone: 'text-emerald-600' },
  { k: 'no_answer', label: 'No Answer', icon: PhoneOff, tone: 'text-slate-400' },
  { k: 'discovery_booked', label: 'Discovery Booked', icon: CalendarClock, tone: 'text-violet-600' },
  { k: 'portals_sent', label: 'Portals Sent', icon: ArrowRight, tone: 'text-sky-600' },
  { k: 'proceeding', label: 'Proceeding', icon: Flame, tone: 'text-amber-600' },
  { k: 'followups_completed', label: 'Follow-Ups Done', icon: ListChecks, tone: 'text-teal-600' },
  { k: 'still_to_contact', label: 'Still To Contact', icon: Target, tone: 'text-rose-600' },
];
const QUICK_GOALS = [
  { label: 'Calls', target: 30 },
  { label: 'Connects', target: 15 },
  { label: 'SMS Conversations', target: 10 },
  { label: 'Leads Contacted', target: 20 },
  { label: 'Appointments Booked', target: 5 },
  { label: 'Clients Moved Forward', target: 3 },
];

const Rate = ({ label, value }) => (
  <div className="flex-1 min-w-[90px]">
    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-lg font-black text-slate-800">{value == null ? '—' : `${value}%`}</p>
  </div>
);

const ProspectingToday = ({ pros, targets, onSetTargets, onLog, onAskAndi, nav }) => {
  const t = pros.tiles || {};
  const r = pros.rates || {};
  const tr = pros.trends || {};
  return (
    <div className="mt-5 bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="myweek-prospecting">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><Phone className="w-4 h-4" /></span>
          <div>
            <h3 className="text-sm font-black text-slate-900">Prospecting Today</h3>
            <p className="text-[11px] text-slate-400">{pros.day} · live scoreboard</p>
          </div>
        </div>
        <button onClick={() => onAskAndi('How is my prospecting going today?')} data-testid="mw-ask-prospecting"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-700">
          <Sparkles className="w-3.5 h-3.5" /> Ask Hey Andi how I'm going
        </button>
      </div>

      {/* Scorecard tiles (live from Lead Nurture) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="mw-prospecting-tiles">
        {PROS_TILES.map(({ k, label, icon: Icon, tone }) => (
          <div key={k} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid={`mw-pros-tile-${k}`}>
            <div className="flex items-center gap-1.5"><Icon className={`w-3.5 h-3.5 ${tone}`} /><p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 leading-none">{label}</p></div>
            <p className="text-2xl font-black text-slate-900 mt-1.5">{t[k] ?? 0}</p>
          </div>
        ))}
      </div>

      {/* Conversion rates */}
      <div className="mt-4 flex items-stretch gap-4 rounded-xl px-4 py-3" style={{ backgroundColor: '#0f172a' }} data-testid="mw-pros-rates">
        <Rate label="Lead → Connect" value={r.lead_to_connect} />
        <div className="w-px bg-white/15" />
        <Rate label="Connect → Discovery" value={r.connect_to_discovery} />
        <div className="w-px bg-white/15" />
        <Rate label="Discovery → Portal" value={r.discovery_to_portal} />
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Daily Targets — custom + manual scoreboard */}
        <div data-testid="mw-pros-targets">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Daily Targets</p>
            <div className="flex items-center gap-1.5">
              <button onClick={onLog} disabled={!targets.length} data-testid="mw-log-activity" className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"><Plus className="w-3 h-3" /> Log Activity</button>
              <button onClick={onSetTargets} data-testid="mw-set-daily-targets" className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-indigo-400 hover:text-indigo-600"><Target className="w-3 h-3" /> Set Daily Targets</button>
            </div>
          </div>
          {targets.length > 0 ? (
            <div className="space-y-2.5">
              {targets.map((g) => {
                const pct = g.pct == null ? 0 : Math.min(100, g.pct);
                const hit = g.target && g.actual >= g.target;
                return (
                  <div key={g.id} data-testid={`mw-target-${g.id}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-bold text-slate-700 flex items-center gap-1.5">{hit && <Trophy className="w-3.5 h-3.5 text-amber-500" />}{g.label}</span>
                      <span className={`font-black ${hit ? 'text-emerald-600' : 'text-slate-800'}`}>{g.actual}/{g.target}</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full mt-1 overflow-hidden"><div className={`h-full rounded-full ${hit ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          ) : <Empty>No daily targets yet. Tap <b>Set Daily Targets</b> to build your scoreboard — or tell Hey Andi “I want 30 calls a day”.</Empty>}
        </div>

        {/* Observations + learning */}
        <div data-testid="mw-pros-observations">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">What MyWeek Is Noticing</p>
          {(pros.observations || []).length > 0 ? (
            <ul className="space-y-2">
              {pros.observations.map((o, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700 leading-snug"><Lightbulb className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />{o}</li>
              ))}
            </ul>
          ) : <Empty>As you log calls and conversations, MyWeek will start coaching you here.</Empty>}
          {pros.has_activity && (
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-500">
              <span>Avg contacts/day: <b className="text-slate-700">{tr.avg_contacts_per_active_day}</b></span>
              <span>Avg connects/day: <b className="text-slate-700">{tr.avg_connects_per_active_day}</b></span>
              <span>This week: <b className="text-slate-700">{tr.this_week_avg}/day</b></span>
              <span>Last week: <b className="text-slate-700">{tr.last_week_avg}/day</b></span>
              {tr.strongest_day && <span>Strongest day: <b className="text-slate-700">{tr.strongest_day}</b></span>}
              {tr.best_hour_window && <span>Best window: <b className="text-slate-700">{tr.best_hour_window}</b></span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SetTargetsModal = ({ targets, onAdd, onUpdate, onDelete, onClose }) => {
  const [label, setLabel] = React.useState('');
  const [target, setTarget] = React.useState('');
  const existing = new Set(targets.map((t) => t.label.toLowerCase()));
  return (
    <Modal onClose={onClose} title="Set Daily Targets" subtitle="Fully customisable — add any metric you want to track" testId="myweek-set-targets">
      <div className="space-y-3">
        {targets.map((t) => (
          <div key={t.id} className="flex items-center gap-2" data-testid={`set-target-row-${t.id}`}>
            <input defaultValue={t.label} onBlur={(e) => e.target.value.trim() && e.target.value !== t.label && onUpdate(t.id, e.target.value.trim(), t.target)} className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <input type="number" defaultValue={t.target} onBlur={(e) => Number(e.target.value) !== t.target && onUpdate(t.id, t.label, Number(e.target.value))} className="w-20 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <button onClick={() => onDelete(t.id)} className="text-slate-300 hover:text-rose-500 p-1" data-testid={`del-target-${t.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="New target (e.g. Calls)" className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="new-target-label" />
          <input value={target} onChange={(e) => setTarget(e.target.value)} type="number" placeholder="Goal" className="w-20 px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="new-target-value" />
          <button onClick={() => { if (label.trim()) { onAdd(label.trim(), Number(target) || 0); setLabel(''); setTarget(''); } }} className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold" data-testid="add-target-btn"><Plus className="w-4 h-4" /></button>
        </div>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {QUICK_GOALS.filter((q) => !existing.has(q.label.toLowerCase())).map((q) => (
            <button key={q.label} onClick={() => onAdd(q.label, q.target)} className="text-[11px] font-bold px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-indigo-400 hover:text-indigo-600" data-testid={`quick-target-${q.label.replace(/\s+/g, '-').toLowerCase()}`}>+ {q.label} ({q.target})</button>
          ))}
        </div>
      </div>
    </Modal>
  );
};

const LogActivityModal = ({ targets, onLog, onClose }) => {
  const [vals, setVals] = React.useState({});
  const set = (id, v) => setVals((p) => ({ ...p, [id]: v }));
  const entries = Object.entries(vals).map(([target_id, count]) => ({ target_id, count: Number(count) || 0 })).filter((e) => e.count !== 0);
  return (
    <Modal onClose={onClose} title="Log Activity" subtitle="Add what you've just done — progress updates instantly" testId="myweek-log-activity">
      <div className="space-y-3">
        {targets.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3" data-testid={`log-row-${t.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-700 truncate">{t.label}</p>
              <p className="text-[11px] text-slate-400">{t.actual}/{t.target} so far</p>
            </div>
            <input type="number" placeholder="+0" value={vals[t.id] ?? ''} onChange={(e) => set(t.id, e.target.value)} className="w-24 px-3 py-2 rounded-lg border border-slate-200 text-sm text-center" data-testid={`log-input-${t.id}`} />
          </div>
        ))}
        <button onClick={() => onLog(entries)} disabled={!entries.length} className="w-full px-3 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40" data-testid="log-activity-save">Add to Today</button>
      </div>
    </Modal>
  );
};


const Modal = ({ title, subtitle, children, onClose, testId }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onClose} data-testid={testId}>
    <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 sticky top-0 bg-white">
        <div><p className="text-sm font-bold text-slate-800">{title}</p>{subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}</div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

function GoalModal({ headers, onClose, onSaved }) {
  const [f, setF] = useState({ title: '', goal_type: 'custom', unit: 'text', target: '', current: '', note: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.title.trim()) return toast.error('Give your goal a title');
    const body = { title: f.title, goal_type: f.goal_type, unit: f.unit, note: f.note,
      target: f.target ? Number(f.target) : null, current: f.current ? Number(f.current) : 0 };
    try { await axios.post(`${API}/api/myweek/goals`, body, { headers }); toast.success('Goal added'); onSaved(); }
    catch { toast.error('Could not add goal'); }
  };
  const inp = 'w-full text-sm px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-indigo-400';
  return (
    <Modal onClose={onClose} title="Add a goal" testId="myweek-goal-modal">
      <div className="space-y-3">
        <input className={inp} placeholder="Goal title — e.g. $10m Lodgement Goal" value={f.title} onChange={set('title')} data-testid="mw-goal-title" />
        <div className="grid grid-cols-2 gap-2">
          <select className={inp} value={f.goal_type} onChange={set('goal_type')} data-testid="mw-goal-type">
            {['lodgement', 'settlement', 'proposal', 'discovery_calls', 'portal_completion', 'lead_conversion', 'revenue', 'working_hours', 'delegation', 'follow_ups', 'productivity', 'custom'].map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
          <select className={inp} value={f.unit} onChange={set('unit')} data-testid="mw-goal-unit">
            {['money', 'percent', 'count', 'days', 'text'].map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        {f.unit !== 'text' && <div className="grid grid-cols-2 gap-2">
          <input className={inp} type="number" placeholder="Current" value={f.current} onChange={set('current')} data-testid="mw-goal-current" />
          <input className={inp} type="number" placeholder="Target" value={f.target} onChange={set('target')} data-testid="mw-goal-target" />
        </div>}
        <input className={inp} placeholder="Note (optional)" value={f.note} onChange={set('note')} />
        <button onClick={save} data-testid="mw-goal-save" className="w-full px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold">Add goal</button>
      </div>
    </Modal>
  );
}

function MemoryEditor({ data, headers, onChange, onDelete }) {
  const [txt, setTxt] = useState('');
  const add = async () => { if (!txt.trim()) return; await axios.post(`${API}/api/myweek/memory`, { text: txt }, { headers }); setTxt(''); onChange(); };
  return (
    <div>
      <div className="flex gap-2 mb-3">
        <input value={txt} onChange={(e) => setTxt(e.target.value)} data-testid="mw-memory-input" placeholder="Tell MyWeek something to remember…" className="flex-1 text-sm px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-indigo-400" />
        <button onClick={add} data-testid="mw-memory-add" className="px-3 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold"><Check className="w-4 h-4" /></button>
      </div>
      {data.length === 0 ? <Empty>Nothing yet. Add your preferences and goals — e.g. "Prefer proposal calls in the mornings".</Empty> :
        <ul className="space-y-2">{data.map((m) => (
          <li key={m.id} className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50" data-testid={`mw-memory-${m.id}`}>
            <span className="flex-1 text-sm text-slate-700">{m.text}</span>
            <span className="text-[10px] text-slate-400">{m.source === 'user' ? 'you' : 'AI'}</span>
            <button onClick={() => onDelete(m.id)} className="text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
          </li>
        ))}</ul>}
    </div>
  );
}
